import { useMutation, useQuery } from '@tanstack/react-query';

/**
 * api.ts — el formulario público de Recolección de Datos. Módulo 18.
 *
 * ## Por qué NO usa `lib/api`
 *
 * Porque quien abre este enlace **no tiene cuenta**. `lib/api` delega en el
 * `API` global, y ahí viven el token y el 401 centralizado que devuelve al
 * login: un invitado de fuera de la organización acabaría en una pantalla de
 * entrada que no puede usar, y el formulario que le enviaron desaparecería sin
 * explicación.
 *
 * Así que aquí es `fetch` pelado, sin cabecera de sesión, igual que lo hacía el
 * armazón. Es la única parte del portal donde eso es lo correcto, y por eso está
 * escrito.
 */

export interface CampoPublico {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  help?: string;
  options?: string[];
}

export interface FormularioPublico {
  form: {
    name: string;
    description: string;
    icon: string;
    color: string;
    fields: CampoPublico[];
  };
  status: string;
  review_note?: string | null;
  invited_name: string;
  data: Record<string, unknown>;
  editable: boolean;
}

/** El token viaja en la sub-ruta del hash: `#formulario?token=…`. */
export function tokenDelHash(): string {
  const h = window.location.hash || '';
  if (!h.startsWith('#formulario')) return '';
  const qs = h.includes('?') ? h.split('?')[1] : '';
  return new URLSearchParams(qs ?? '').get('token') ?? '';
}

async function pedir<T>(ruta: string, init?: RequestInit): Promise<T> {
  const res = await fetch(ruta, init);
  // El cuerpo se lee ANTES de mirar `res.ok`: el servidor manda el motivo en
  // `detail` y perderlo deja al invitado con «no se pudo enviar» a secas.
  let cuerpo: unknown = null;
  try {
    cuerpo = await res.json();
  } catch {
    // Un cuerpo que no es JSON no añade nada a un error que ya está en curso.
  }
  if (!res.ok) {
    const detalle = (cuerpo as { detail?: string } | null)?.detail;
    const err: Error & { status?: number } = new Error(detalle || 'Enlace no válido');
    err.status = res.status;
    throw err;
  }
  return cuerpo as T;
}

export function useFormularioPublico(token: string) {
  return useQuery({
    queryKey: ['publico', 'formulario', token] as const,
    queryFn: () => pedir<FormularioPublico>(`/api/public/forms/${encodeURIComponent(token)}`),
    enabled: Boolean(token),
    // Un enlace vencido no mejora reintentando, y cada intento consume del
    // límite de 30/min que el servidor reparte entre TODOS los invitados.
    retry: false,
  });
}

export function useEnviarFormularioPublico(token: string) {
  return useMutation({
    mutationFn: (datos: Record<string, string>) =>
      pedir<{ message?: string }>(`/api/public/forms/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: datos }),
      }),
  });
}
